import { inject, Injectable } from '@angular/core';
import {
  ApiResponse,
  ApiResponseReferenceMaterialsPaginated,
  ApiResponseQuestionPaginated,
  behaviorGoals,
  behaviorGoalsModel,
  FrequencySetting,
  FrequencySettingPayload,
  lmsSettingModel,
  MaterialParams,
  QuestionPayload,
  ReferenceMaterialModel,
  TOkenData,
  QuestionItem,
  MaterialParamsQuestionnaire,
  CustomQuestionSequenceNumbers,
  PlanPayload,
  ApiResponsePlanPaginated,
  QuestionnaireResponse,
} from './manage-settings.interface';
import { environment } from '../../../environments/environment.development';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ErrorHandlingService } from '../../core/services/error-handling.service';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ManageSettingsService {
  private readonly BASE_URL = environment.apiUrl;
  private readonly PLAN_BASE_URL =
    environment.apiUrl + '/plantypemaster/plan-type';
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);
  private referenceMaterialSubject =
    new BehaviorSubject<ApiResponseReferenceMaterialsPaginated | null>(null);
  readonly referenceMaterials$ = this.referenceMaterialSubject.asObservable();
  private questionSubject =
    new BehaviorSubject<ApiResponseQuestionPaginated | null>(null);
  readonly questions$ = this.questionSubject.asObservable();

  decodeTokenManual(token: string): TOkenData | null {
    try {
      const base64Url = token.split('.')[1]; // Get payload
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(base64));
    } catch (error) {
      console.error('Invalid token', error);
      return null;
    }
  }

  saveLmsSettings(payload: lmsSettingModel) {
    return this.http
      .post<
        ApiResponse<void>
      >(`${this.BASE_URL}/OrganizationLmsSettings/create`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getLmsSettingsByOrg(org: number) {
    return this.http
      .get<
        ApiResponse<lmsSettingModel>
      >(`${this.BASE_URL}/OrganizationLmsSettings/get-by-org-id?organizationId=${org}`)
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getReferenceMaterials(
    params: MaterialParams
  ): Observable<ApiResponseReferenceMaterialsPaginated> {
    return this.http
      .get<ApiResponseReferenceMaterialsPaginated>(
        `${this.BASE_URL}/OrganizationReferenceMaterial/list`,
        {
          params: this.buildQueryParams(params),
        }
      )
      .pipe(
        tap(response => this.referenceMaterialSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  private buildQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sort,
    organizationId,
  }: MaterialParams): HttpParams {
    let params = new HttpParams()
      .set('Page', pageIndex.toString())
      .set('PageSize', pageSize.toString());

    if (sort) {
      params = params.set('sort', sort);
    }

    if (sortOrder !== undefined) {
      params = params.set('sortOrder', sortOrder.toString());
    }
    if (organizationId) {
      params = params.set('organizationId', organizationId.toString());
    }

    return params;
  }

  createMaterial({
    organizationId,
    name,
    description,
    isPublished,
    file,
  }: ReferenceMaterialModel): Observable<ApiResponse<void>> {
    const formData = new FormData();
    formData.append('organizationId', organizationId.toString());
    formData.append('Name', name);
    formData.append('Description', description);
    formData.append('isPublished', isPublished.toString());
    formData.append('File', file);

    return this.http
      .post<
        ApiResponse<void>
      >(`${this.BASE_URL}/OrganizationReferenceMaterial/upload`, formData)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteReferenceMaterial(id: number): Observable<ApiResponse<void>> {
    return this.http
      .delete<
        ApiResponse<void>
      >(`${this.BASE_URL}/OrganizationReferenceMaterial/${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  saveBehvaiorGoals(payload: behaviorGoalsModel) {
    return this.http
      .post<
        ApiResponse<void>
      >(`${this.BASE_URL}/OrganizationBehaviourGoal/create`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getbehaviorGoalsByOrgId(org: number) {
    return this.http
      .get<
        ApiResponse<behaviorGoals>
      >(`${this.BASE_URL}/OrganizationBehaviourGoal/get-by-org-id?organizationId=${org}`)
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getFrequencySettingsByOrg(org: number) {
    return this.http
      .get<
        ApiResponse<FrequencySetting>
      >(`${this.BASE_URL}/organizationcasefeedback/get-configuration?organizationId=${org}`)
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }
  saveFrequencySettings(payload: FrequencySettingPayload) {
    return this.http
      .post<
        ApiResponse<void>
      >(`${this.BASE_URL}/organizationcasefeedback/save-configuration`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  private buildQuestionQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sort,
    plantypeId,
    ParentOrganizationId,
    IsActive,
    QuestionText,
  }: MaterialParamsQuestionnaire): HttpParams {
    let params = new HttpParams()
      .set('Page', pageIndex.toString())
      .set('PageSize', pageSize.toString());

    if (sort) params = params.set('sort', sort);
    if (sortOrder !== undefined)
      params = params.set('sortOrder', sortOrder.toString());
    if (ParentOrganizationId)
      params = params.set(
        'ParentOrganizationId',
        ParentOrganizationId.toString()
      );
    if (IsActive !== undefined)
      params = params.set('IsActive', IsActive.toString());
    if (QuestionText) params = params.set('QuestionText', QuestionText);
    if (plantypeId) params = params.set('plantypeId', plantypeId.toString());

    return params;
  }

  saveQuestion(payload: QuestionPayload) {
    return this.http
      .post<
        ApiResponse<void>
      >(`${this.BASE_URL}/organizationquestionmaster/create-org-question-master`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getAlltheQuestions(
    params: MaterialParamsQuestionnaire
  ): Observable<ApiResponseQuestionPaginated> {
    return this.http
      .get<ApiResponseQuestionPaginated>(
        `${this.BASE_URL}/organizationquestionmaster/get-org-question-master-list`,
        {
          params: this.buildQuestionQueryParams(params),
        }
      )
      .pipe(
        tap(response => this.questionSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getQuestionsByPlanType(
    planTypeId: number
  ): Observable<ApiResponse<QuestionnaireResponse[]>> {
    return this.http
      .get<
        ApiResponse<QuestionnaireResponse[]>
      >(`${this.BASE_URL}/organizationquestionmaster/get-org-question-by-plantype?planTypeId=${planTypeId}`)
      .pipe(
        tap(response => {
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getQuestionById(id: number): Observable<ApiResponse<QuestionItem>> {
    return this.http
      .get<
        ApiResponse<QuestionItem>
      >(`${this.BASE_URL}/organizationquestionmaster/get-question-master-by-id?id=${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  updateQuestion(payload: QuestionPayload) {
    return this.http
      .put<
        ApiResponse<void>
      >(`${this.BASE_URL}/organizationquestionmaster/update`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteQuestion(id: number): Observable<ApiResponse<void>> {
    return this.http
      .delete<
        ApiResponse<void>
      >(`${this.BASE_URL}/organizationquestionmaster/${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  updateSequenceNumber(payload: CustomQuestionSequenceNumbers) {
    return this.http
      .put<
        ApiResponse<void>
      >(`${this.BASE_URL}/organizationquestionmaster/update-question-master-sequence-number`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteOrganizationQuestionOption(id: number): Observable<ApiResponse<void>> {
    return this.http
      .delete<
        ApiResponse<void>
      >(`${this.BASE_URL}/organizationquestionmaster/delete-organization-question-option?id=${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  savePlan(payload: PlanPayload) {
    return this.http
      .post<ApiResponse<void>>(`${this.PLAN_BASE_URL}/create`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getAllPlans(organizationId: number): Observable<ApiResponsePlanPaginated> {
    const params = new HttpParams().set(
      'organizationId',
      organizationId.toString()
    );
    return this.http
      .get<ApiResponsePlanPaginated>(`${this.PLAN_BASE_URL}/list`, { params })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  updatePlan(payload: PlanPayload) {
    return this.http
      .put<ApiResponse<void>>(`${this.PLAN_BASE_URL}/update`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getPlanById(id: number): Observable<ApiResponse<PlanPayload>> {
    return this.http
      .get<ApiResponse<PlanPayload>>(`${this.PLAN_BASE_URL}/get-by-id?id=${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
}
